import storage from "./../storage";

interface OpportunisticFormState {
  cvosLogin: null | string;
  type: null | string;
  formId: null | number;
  requiresPlace: boolean;
  forms: Form[];
  campaignId: null | number;
  campaigns: Campaign[];
  search: string;
  filteredOpts: (Form | Campaign)[];
  openOpts: boolean;
  placeAddress: null | string;
  placeCity: null | string;
  placeName: null | string;
  placePhone: null | string;
  placeType: null | string;
  placeZipcode: null | string;
  step: number;
  errors: Errors;
  selectForm: (form: Form) => void;
  selectCampaign: (camapign: Campaign) => void;
  initFilteredOptions: () => void;
  filterOptions: (items: Campaign[] | Form[]) => void;
  setFormId: () => void;
  back: () => void;
  validateStep1: () => void;
  validateStep2: () => void;
}

interface Errors {
  placeAddress?: string;
  placeCity?: string;
  placeName?: string;
  placePhone?: string;
  placeType?: string;
  placeZipcode?: string;
}

interface Campaign {
  id: number;
  name: string;
  form_id: number;
}

interface Form {
  id: number;
  name: string;
  requires_place: boolean;
}

type ValidatableField = keyof Pick<
  OpportunisticFormState,
  "placeAddress" | "placeCity" | "placeName" | "placePhone" | "placeType" | "placeZipcode"
>;

function setFormId(this: OpportunisticFormState) {
  const campaign = this.campaigns.find((c) => String(c.id) === String(this.campaignId));

  this.formId = campaign?.form_id || null;
}

function back(this: OpportunisticFormState) {
  this.step--;
}

function validateStep1(this: OpportunisticFormState) {
  if (this.requiresPlace) {
    this.step = 2;
  } else {
    storage.setItem(
      {
        id: "task-opportunistic",
        cvoslogin: this.cvosLogin,
        requiresPlace: this.requiresPlace,
        context: { form_id: this.formId },
      },
      { storeName: "tasks" },
    );
    this.step = 3;
  }
}

function validateStep2(this: OpportunisticFormState) {
  if (this.placeType) {
    const requiredFields: ValidatableField[] = getRequiredFields(this.placeType);
    const errors: Errors = {};

    requiredFields.forEach((field) => {
      if (!this[field]?.trim()) {
        errors[field] = "Champ obligatoire";
      }
    });

    // Add specific validation for phone and zipcode
    if (this.placePhone && !/^(0|\+33)[1-9](\d{8})$/.test(this.placePhone.replace(/\s/g, ""))) {
      errors.placePhone = "Téléphone invalide";
    }

    if (this.placeZipcode && !/^\d{5}$/.test(this.placeZipcode)) {
      errors.placeZipcode = "Code postal invalide";
    }

    this.errors = errors;

    if (Object.keys(this.errors).length === 0) {
      storage.setItem(
        {
          id: "task-opportunistic",
          cvoslogin: this.cvosLogin,
          requiresPlace: this.requiresPlace,
          context: {
            form_id: this.formId,
            campaign_id: this.campaignId,
            name: this.placeName,
            place_phone: this.placePhone,
            place_type: this.placeType,
            address: this.placeAddress,
            commune: this.placeCity,
            code_postal: this.placeZipcode,
          },
        },
        { storeName: "tasks" },
      );
      this.step = 3;
    }
  } else {
    this.errors = { placeType: "Champ obligatoire" };
  }
}

function initFilteredOptions(this: OpportunisticFormState) {
  const items = this.type === "campaign" ? this.campaigns : this.forms;
  this.search = "";
  this.requiresPlace = true;
  this.filterOptions(items);
}

function filterOptions(this: OpportunisticFormState, items: (Form | Campaign)[]) {
  this.filteredOpts = items
    .filter((item) => item.name.toLowerCase().includes(this.search.toLowerCase()))
    .slice(0, 5);
}

function getRequiredFields(placeType: string | null): ValidatableField[] {
  switch (placeType) {
    case "address":
      return ["placeAddress", "placeCity", "placeZipcode"];
    case "location":
      return ["placeCity", "placeZipcode"];
    default:
      return ["placeAddress", "placeCity", "placeName", "placePhone", "placeZipcode"];
  }
}

function alpineData({
  campaigns,
  forms,
  cvosLogin,
}: {
  campaigns: Campaign[];
  forms: Form[];
  cvosLogin: null | string;
}) {
  return {
    cvosLogin: cvosLogin,
    type: null,
    formId: null,
    requiresPlace: true,
    forms: forms,
    campaignId: null,
    campaigns: campaigns,
    search: "",
    filteredOpts: [],
    openOpts: false,
    placeAddress: null,
    placeCity: null,
    placeName: null,
    placePhone: null,
    placeType: null,
    placeZipcode: null,
    step: 1,
    errors: {},
    selectForm(form: Form) {
      this.formId = form.id;
      this.requiresPlace = form.requires_place;
      this.search = form.name;
      this.openOpts = false;
    },
    selectCampaign(campaign: Campaign) {
      this.campaignId = campaign.id;
      this.search = campaign.name;
      this.openOpts = false;
      this.setFormId();
    },
    initFilteredOptions,
    filterOptions,
    setFormId,
    back,
    validateStep1,
    validateStep2,
  } as OpportunisticFormState;
}

export default alpineData;
